import { useCallback, useMemo, useState } from 'react';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Button,
  OutlinedSelect,
  OutlinedSelectOptionType,
  addToast,
  showDialogConfirm,
  TextOutlinedInput,
  Icon,
} from '@octano/global-ui';
import { Col, Row, Table } from 'reactstrap';
import {
  AdmissionScope,
  AdmissionViaOption,
  AvailableOffer,
  BulkAdmissionsBody,
  BulkPreviewResult,
  Offer,
} from '../../types';
import { bulkPreviewAction, bulkSaveAction } from '../managementActions';
import { formatToOnlyPositiveIntegers } from '../../../../../utils/currency';
import BulkOffersQuotaWarningModal from './BulkOffersQuotaWarningModal';
import { ALL_ADMISSION_SCOPES_VALUE, normalizeScope } from './bulkOffersScope';
import {
  hasSeparateQuotaColumn,
  orderViaColumns,
  splitCellTotals,
  ViaColumn,
} from './bulkOffersColumns';
import {
  bulkRowKey,
  deriveQuotaConflicts,
  QuotaConflicts,
} from './bulkOffersConflicts';

interface Cell {
  admissionId?: number;
  quota: string;
}

interface OfferRow {
  rowKey: string;
  offerId: number;
  scopeValue: string; // 'OS' | 'OL' | 'OS,OL'
  cells: Cell[]; // aligned to viaColumns order
}

interface Props {
  processId: number;
  offers: Offer[];
  availableOffers: AvailableOffer[];
  admissionVias: AdmissionViaOption[];
  academicOfferQuota: number | null;
  refresh: () => void;
  onDone: () => void;
}

let keyCounter = 0;
const nextKey = () => `row_${keyCounter++}`;

export default function BulkOffersEditor({
  processId,
  offers,
  availableOffers,
  admissionVias,
  academicOfferQuota,
  onDone,
  refresh,
}: Props) {
  const { t } = useTranslation('academic-offer/admission-processes');
  const [preview, setPreview] = useState<BulkPreviewResult | null>(null);
  const [removedAdmissionIds] = useState<number[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [quotaWarning, setQuotaWarning] = useState<QuotaConflicts | null>(null);

  // Add-offer panel state
  const [addOfferId, setAddOfferId] = useState<OutlinedSelectOptionType | null>(
    null,
  );
  const [addScope, setAddScope] = useState<OutlinedSelectOptionType | null>(
    null,
  );

  // Vias become columns, separate-quota ones first. If there are no vias, use
  // a single "Cupos" column, which always deducts.
  const viaColumns = useMemo<ViaColumn[]>(
    () =>
      admissionVias.length > 0
        ? orderViaColumns(
            admissionVias.map((v) => ({
              id: v.id,
              name: v.name,
              deductsOfferQuota: v.deductsOfferQuota,
            })),
          )
        : [{ id: '', name: t('offersTable.quota'), deductsOfferQuota: true }],
    [admissionVias, t],
  );

  const showSeparateQuotaColumn = useMemo(
    () => hasSeparateQuotaColumn(viaColumns),
    [viaColumns],
  );

  // Index of the first deducting column: the separate-quota subtotal column is
  // rendered right before it.
  const firstDeductingIndex = useMemo(
    () => viaColumns.filter((c) => !c.deductsOfferQuota).length,
    [viaColumns],
  );

  const colViaId = (colId: string): string | null =>
    colId === '' ? null : colId;

  const offerLabelById = useMemo(() => {
    const map = new Map<number, string>();
    availableOffers.forEach((o) =>
      map.set(o.id, `${o.studyPlanName} - ${o.campusName} - ${o.scheduleName}`),
    );
    return map;
  }, [availableOffers]);

  // Cupos declarados por oferta (programa-sede-jornada). `offers` cubre las
  // filas ya cargadas y `availableOffers` las que se pueden agregar desde el
  // panel de arriba; ambas ya traen `offerQuota` del backend. Se normaliza a
  // number porque `numeric` de Postgres llega serializado como string, y
  // sumarlo sin convertir concatena en vez de sumar.
  const offerQuotaById = useMemo(() => {
    const map = new Map<number, number | null>();
    const parseQuota = (value: number | string | null): number | null =>
      value == null ? null : Number(value);
    offers.forEach((o) => {
      if (o.offerId != null) {
        map.set(o.offerId, parseQuota(o.offerQuota));
      }
    });
    availableOffers.forEach((o) => {
      if (!map.has(o.id)) {
        map.set(o.id, parseQuota(o.offerQuota));
      }
    });
    return map;
  }, [offers, availableOffers]);

  // Stable map of every existing admission id per offer (including admissions
  // on vias not shown as a column). Derived from the loaded offers, not stored
  // in the form, so it can't be dropped by react-hook-form. Used to fully
  // remove an offer when its whole row is left at 0.
  const admissionIdsByOffer = useMemo(() => {
    const map = new Map<number, number[]>();
    offers.forEach((o) => {
      if (o.offerId == null) {
        return;
      }
      const id = Number(o.admissionId);
      if (Number.isNaN(id)) {
        return;
      }
      const list = map.get(o.offerId) ?? [];
      list.push(id);
      map.set(o.offerId, list);
    });
    return map;
  }, [offers]);

  const scopeOptions = useMemo(
    () => [
      { value: AdmissionScope.OS, label: t('utils.admissionScopes.OS') },
      { value: AdmissionScope.OL, label: t('utils.admissionScopes.OL') },
      {
        value: ALL_ADMISSION_SCOPES_VALUE,
        label: t('utils.admissionScopes.OS_OL'),
      },
    ],
    [t],
  );

  const initialRows: OfferRow[] = useMemo(() => {
    const byOffer = new Map<number, Offer[]>();
    offers.forEach((o) => {
      if (o.offerId == null) {
        return;
      }
      const list = byOffer.get(o.offerId) ?? [];
      list.push(o);
      byOffer.set(o.offerId, list);
    });
    return Array.from(byOffer.entries()).map(([offerId, admissions]) => {
      const first = admissions[0];
      return {
        rowKey: nextKey(),
        offerId,
        scopeValue: normalizeScope(first.admissionScopes),
        cells: viaColumns.map((col) => {
          const target = colViaId(col.id);
          const adm = admissions.find(
            (a) => (a.admissionViaId ?? null) === target,
          );
          return adm
            ? {
                admissionId: Number(adm.admissionId),
                quota: adm.quota?.toString() ?? '',
              }
            : { quota: '' };
        }),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { control, getValues } = useForm<{ rows: OfferRow[] }>({
    defaultValues: { rows: initialRows },
  });
  const { fields, append } = useFieldArray({ control, name: 'rows' });
  const watchedRows = useWatch({ control, name: 'rows' });

  const usedOfferIds = useMemo(
    () => new Set((watchedRows ?? []).map((r) => r.offerId)),
    [watchedRows],
  );

  const addableOfferOptions = useMemo<OutlinedSelectOptionType[]>(
    () =>
      availableOffers
        .filter((o) => !usedOfferIds.has(o.id))
        .map((o) => ({
          value: o.id,
          label: `${o.studyPlanName} - ${o.campusName} - ${o.scheduleName}`,
        })),
    [availableOffers, usedOfferIds],
  );

  const buildBody = useCallback((): BulkAdmissionsBody => {
    const rows = getValues('rows');
    const upserts: BulkAdmissionsBody['upserts'] = [];
    const deletes: number[] = [...removedAdmissionIds];
    rows.forEach((row) => {
      const scopes = row.scopeValue
        ? (row.scopeValue.split(',') as AdmissionScope[])
        : [];
      const parseCell = (raw: string) =>
        raw == null || raw === '' ? NaN : Number(raw);
      const anyPositive = row.cells.some((cell) => {
        const n = parseCell(cell.quota);
        return !Number.isNaN(n) && n > 0;
      });

      // Whole row at 0/empty: remove the offer entirely, including admissions
      // on vias not shown as a column (orphans).
      if (!anyPositive) {
        (admissionIdsByOffer.get(row.offerId) ?? []).forEach((id) =>
          deletes.push(id),
        );
        return;
      }

      // Row has cupos: upsert positive cells, and only clear (delete) the
      // mapped cells the user emptied. Orphan admissions are left untouched.
      row.cells.forEach((cell, c) => {
        const viaId = colViaId(viaColumns[c].id);
        const parsed = parseCell(cell.quota);
        const hasPositiveQuota = !Number.isNaN(parsed) && parsed > 0;
        if (hasPositiveQuota) {
          upserts.push({
            rowKey: bulkRowKey(row.offerId, viaId),
            id: cell.admissionId,
            offerId: row.offerId,
            admissionViaId: viaId,
            quota: parsed,
            admissionScopes: scopes,
          });
        } else if (cell.admissionId != null) {
          deletes.push(cell.admissionId);
        }
      });
    });
    return { upserts, deletes: Array.from(new Set(deletes)) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getValues, removedAdmissionIds, viaColumns, admissionIdsByOffer]);

  const allErrors = useMemo(() => {
    const errors = new Set<string>();
    preview?.rows.forEach((r) => r.errors.forEach((e) => errors.add(e)));
    return Array.from(errors);
  }, [preview]);

  // Totales por fila (oferta), en el mismo orden que `fields`/`watchedRows`.
  // "deducting" es lo que se compara contra el cupo declarado de la oferta;
  // "separate" son los cupos que van por encima de él.
  const rowTotals = useMemo(
    () =>
      (watchedRows ?? []).map((row) =>
        splitCellTotals(row.cells ?? [], viaColumns),
      ),
    [watchedRows, viaColumns],
  );

  // Qué conflictos hay lo dice el preview (es el backend el que decide si el
  // guardado se pasa de cupo); las cifras salen del formulario, que es lo que
  // el usuario acaba de escribir y lo que la tabla ya muestra.
  const computeQuotaConflicts = useCallback(
    (previewResult: BulkPreviewResult): QuotaConflicts =>
      deriveQuotaConflicts({
        previewRows: previewResult.rows,
        rows: getValues('rows'),
        viaColumns,
        offerQuotaById,
        offerLabelById,
        academicOfferQuota,
      }),
    [
      getValues,
      offerQuotaById,
      offerLabelById,
      academicOfferQuota,
      viaColumns,
    ],
  );

  const handleAddOfferRow = () => {
    if (!addOfferId) {
      return;
    }
    append({
      rowKey: nextKey(),
      offerId: Number(addOfferId.value),
      scopeValue: addScope
        ? String(addScope.value)
        : ALL_ADMISSION_SCOPES_VALUE,
      cells: viaColumns.map(() => ({ quota: '' })),
    });
    setAddOfferId(null);
    setAddScope(null);
  };

  const runPreview =
    useCallback(async (): Promise<BulkPreviewResult | null> => {
      const result = await bulkPreviewAction({
        id: processId,
        data: buildBody(),
      });
      if (!result.success) {
        addToast({ text: t('bulkOffers.saveError'), color: 'danger' });
        return null;
      }
      setPreview(result.data as BulkPreviewResult);
      return result.data as BulkPreviewResult;
    }, [processId, buildBody, t]);

  const doSave = useCallback(async () => {
    setIsSaving(true);
    const result = await bulkSaveAction({ id: processId, data: buildBody() });
    if (!result.success) {
      addToast({ text: t('bulkOffers.saveError'), color: 'danger' });
      setIsSaving(false);
      return;
    }
    addToast({ text: t('bulkOffers.saveSuccess'), color: 'success' });
    refresh();
    onDone();
    setIsSaving(false);
  }, [processId, buildBody, refresh, onDone, t]);

  const handleSave = async () => {
    const previewResult = await runPreview();
    if (!previewResult) {
      return;
    }
    if (previewResult.summary.hasErrors) {
      addToast({ text: t('bulkOffers.saveError'), color: 'danger' });
      return;
    }
    if (previewResult.summary.hasWarnings) {
      setQuotaWarning(computeQuotaConflicts(previewResult));
      return;
    }
    showDialogConfirm({
      icon: { name: 'warning', color: 'warning' },
      title: t('bulkOffers.title'),
      subtitle: t('bulkOffers.confirmSave'),
      btnConfirm: { text: t('bulkOffers.save'), onConfirm: doSave },
      btnClose: { text: t('bulkOffers.cancel') },
    });
  };

  // Totals
  const colTotals = viaColumns.map((_, c) =>
    (watchedRows ?? []).reduce(
      (sum, row) => sum + (Number(row.cells?.[c]?.quota) || 0),
      0,
    ),
  );
  const grandTotal = colTotals.reduce(
    (sum, tot, c) => (viaColumns[c].deductsOfferQuota ? sum + tot : sum),
    0,
  );
  const separateGrandTotal = colTotals.reduce(
    (sum, tot, c) => (viaColumns[c].deductsOfferQuota ? sum : sum + tot),
    0,
  );
  const overCap = academicOfferQuota != null && grandTotal > academicOfferQuota;

  // Suma de cupos declarados (columna "Cupos Oferta") para las ofertas
  // presentes en la tabla. Si alguna no tiene cupo declarado (sin límite),
  // la suma pierde sentido y se muestra "∞" en su lugar.
  const hasUnboundedOfferQuota = (watchedRows ?? []).some(
    (row) => offerQuotaById.get(row.offerId) == null,
  );
  const offerQuotaGrandTotal = hasUnboundedOfferQuota
    ? null
    : (watchedRows ?? []).reduce(
        (sum, row) => sum + (offerQuotaById.get(row.offerId) ?? 0),
        0,
      );

  const stickyBase: React.CSSProperties = {
    position: 'sticky',
    left: 0,
    zIndex: 1,
    minWidth: 240,
  };
  const stickyHead: React.CSSProperties = {
    ...stickyBase,
    zIndex: 2,
  };

  return (
    <>
      <div
        className="border rounded p-3 mt-4 mb-4"
        style={{ position: 'relative', zIndex: 10 }}
      >
        <p className="fs-14 font-weight-bold text-primary mb-2">
          {t('bulkOffers.addOfferTitle')}
        </p>
        <Row className="align-items-end">
          <Col xs={12} md={4}>
            <OutlinedSelect
              name="add_offer"
              label={t('offersTable.offer')}
              options={addableOfferOptions}
              value={addOfferId ?? undefined}
              onChange={(v: any) => setAddOfferId(v ?? null)}
              isClearable
              noOptionsMessage={t('offersTable.noOffersToAdd')}
            />
          </Col>
          <Col xs={6} md={4}>
            <OutlinedSelect
              name="add_scope"
              label={t('offersTable.enrollmentType')}
              options={scopeOptions}
              value={addScope ?? undefined}
              onChange={(v: any) => setAddScope(v ?? null)}
            />
          </Col>
          <Col xs={12} md={4}>
            <Button
              text={t('bulkOffers.addRow')}
              size="md"
              fullwidth
              disabled={!addOfferId}
              className="mb-3"
              onClick={handleAddOfferRow}
            />
          </Col>
        </Row>
      </div>

      <div className="d-flex flex-wrap justify-content-between align-items-center mb-2">
        <span className="fs-16 font-weight-bold text-primary">
          {t('bulkOffers.rowsTitle')}
        </span>
        <span
          className={`badge badge-pill fw-400 fs-14 d-flex align-items-center badge-white border w-auto px-3 py-2 ${
            overCap ? 'text-danger' : 'text-dark'
          }`}
          style={{ gap: 5 }}
        >
          <Icon name="information" color="primary" />
          {t('bulkOffers.totals', {
            total: grandTotal,
            cap: academicOfferQuota ?? '∞',
          })}
        </span>
      </div>

      <div className="mb-3 table-responsive border rounded">
        <Table className="mb-0 align-middle table-sm" borderless striped>
          <thead>
            <tr>
              <th
                className="fs-13 text-uppercase bg-secondary fw-400 text-white border-0"
                style={stickyHead}
              >
                {t('offersTable.offer')}
              </th>
              {viaColumns.slice(0, firstDeductingIndex).map((col) => (
                <th
                  key={col.id || 'single'}
                  className="fs-13 text-center bg-secondary fw-400 text-white align-middle border-0"
                  style={{ minWidth: 96 }}
                >
                  {col.name}
                </th>
              ))}
              {showSeparateQuotaColumn && (
                <th
                  className="fs-13 text-center bg-secondary fw-400 text-white align-middle border-0"
                  style={{ minWidth: 120 }}
                >
                  {t('bulkOffers.separateQuotaTotalColumn')}
                </th>
              )}
              {viaColumns.slice(firstDeductingIndex).map((col) => (
                <th
                  key={col.id || 'single'}
                  className="fs-13 text-center bg-secondary fw-400 text-white align-middle border-0"
                  style={{ minWidth: 96 }}
                >
                  {col.name}
                </th>
              ))}
              <th
                className="fs-13 text-center bg-secondary fw-400 text-white border-0"
                style={{ minWidth: 80 }}
              >
                {t('bulkOffers.total')}
              </th>
              <th
                className="fs-13 text-center bg-secondary fw-400 text-white border-0"
                style={{ minWidth: 80 }}
              >
                {t('bulkOffers.offerQuotaColumn')}
              </th>
            </tr>
          </thead>
          <tbody>
            {fields.length === 0 && (
              <tr>
                <td
                  colSpan={
                    viaColumns.length + (showSeparateQuotaColumn ? 4 : 3)
                  }
                  className="text-center text-muted py-4 fs-14"
                >
                  {t('bulkOffers.empty')}
                </td>
              </tr>
            )}
            {fields.map((field, index) => {
              const totals = rowTotals[index] ?? { deducting: 0, separate: 0 };
              const rowTotal = totals.deducting;
              const offerId = watchedRows?.[index]?.offerId ?? field.offerId;
              const offerQuota = offerQuotaById.get(offerId) ?? null;
              const totalColorClass =
                offerQuota == null
                  ? undefined
                  : rowTotal > offerQuota
                    ? 'text-danger'
                    : rowTotal < offerQuota
                      ? 'text-primary'
                      : undefined;
              return (
                <tr key={field.id}>
                  <td
                    style={{
                      ...stickyBase,
                      // Flat equivalent of the row's own rgba striping (not
                      // the same rgba value), since this cell paints its
                      // background on top of the already-striped <tr> and
                      // would otherwise double up the tint.
                      backgroundColor:
                        index % 2 === 0 ? 'rgb(242, 242, 242)' : '#fff',
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="fs-14 mb-1 pr-2">
                        {`${offerLabelById.get(offerId) ?? `#${offerId}`} `}
                      </div>
                    </div>
                  </td>
                  {viaColumns.slice(0, firstDeductingIndex).map((col, c) => (
                    <td
                      key={col.id || 'single'}
                      className="text-center align-middle"
                    >
                      <Controller
                        control={control}
                        name={`rows.${index}.cells.${c}.quota`}
                        render={({ field }) => (
                          <TextOutlinedInput
                            name={field.name}
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            placeholder="0"
                            formatter={formatToOnlyPositiveIntegers}
                          />
                        )}
                      />
                    </td>
                  ))}
                  {showSeparateQuotaColumn && (
                    <td
                      className="text-center font-weight-bold"
                      style={{ verticalAlign: 'middle' }}
                    >
                      {totals.separate}
                    </td>
                  )}
                  {viaColumns.slice(firstDeductingIndex).map((col, i) => {
                    const c = firstDeductingIndex + i;
                    return (
                      <td
                        key={col.id || 'single'}
                        className="text-center align-middle"
                      >
                        <Controller
                          control={control}
                          name={`rows.${index}.cells.${c}.quota`}
                          render={({ field }) => (
                            <TextOutlinedInput
                              name={field.name}
                              value={field.value}
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                              placeholder="0"
                              formatter={formatToOnlyPositiveIntegers}
                            />
                          )}
                        />
                      </td>
                    );
                  })}
                  <td
                    className={`text-center font-weight-bold ${
                      totalColorClass ?? ''
                    }`}
                    style={{ verticalAlign: 'middle' }}
                  >
                    <div className="mb-2">{rowTotal}</div>
                  </td>
                  <td
                    className="text-center font-weight-bold"
                    style={{ verticalAlign: 'middle' }}
                  >
                    <div className="mb-2">{offerQuota ?? '∞'}</div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          {fields.length > 0 && (
            <tfoot>
              <tr className="bg-tertiary" style={{ height: 60 }}>
                <td
                  className="bg-tertiary font-weight-bold text-primary"
                  style={{ ...stickyBase, height: 60, verticalAlign: 'middle' }}
                >
                  {t('bulkOffers.total')}
                </td>
                {colTotals.slice(0, firstDeductingIndex).map((tot, c) => (
                  <td
                    key={viaColumns[c].id || 'single'}
                    className="text-center font-weight-bold"
                    style={{ verticalAlign: 'middle' }}
                  >
                    {tot}
                  </td>
                ))}
                {showSeparateQuotaColumn && (
                  <td
                    className="text-center font-weight-bold"
                    style={{ verticalAlign: 'middle' }}
                  >
                    {separateGrandTotal}
                  </td>
                )}
                {colTotals.slice(firstDeductingIndex).map((tot, i) => (
                  <td
                    key={viaColumns[firstDeductingIndex + i].id || 'single'}
                    className="text-center font-weight-bold"
                    style={{ verticalAlign: 'middle' }}
                  >
                    {tot}
                  </td>
                ))}
                <td
                  className="text-center font-weight-bold text-primary"
                  style={{ verticalAlign: 'middle' }}
                >
                  {grandTotal}
                </td>
                <td
                  className="text-center font-weight-bold"
                  style={{ verticalAlign: 'middle' }}
                >
                  {offerQuotaGrandTotal ?? '∞'}
                </td>
              </tr>
            </tfoot>
          )}
        </Table>
      </div>

      {allErrors.length > 0 && (
        <div className="mb-3">
          {allErrors.map((code) => (
            <small key={code} className="d-block text-danger">
              {t(`bulkOffers.errors.${code}`)}
            </small>
          ))}
        </div>
      )}
      <Row className="align-items-center">
        <Col
          xs={12}
          className="d-flex flex-wrap justify-content-md-end mb-2"
          style={{ gap: 8 }}
        >
          <Button
            text={t('bulkOffers.cancel')}
            outlined
            size="md"
            onClick={onDone}
            disabled={isSaving}
          />
          <Button
            text={t('bulkOffers.save')}
            size="md"
            className="px-5 font-weight-bold"
            loading={isSaving}
            disabled={isSaving}
            onClick={handleSave}
          />
        </Col>
      </Row>

      <BulkOffersQuotaWarningModal
        show={quotaWarning != null}
        offerConflicts={quotaWarning?.offers ?? []}
        processConflict={quotaWarning?.process ?? null}
        onCloseModal={() => setQuotaWarning(null)}
        onConfirm={() => {
          setQuotaWarning(null);
          doSave();
        }}
      />
    </>
  );
}
